/**
 * Texts on the Company's number (go-service/internal/api/messages.go,
 * #207 step 4).
 *
 * Its own file, beside softphone-api: nothing here is a setting. A
 * thread is the Company's number and the number at the other end, and
 * that pair is the whole address — nothing is created to start one.
 * The API answers 503 where no carrier is wired, which the tab reads as
 * "texts are not set up here", exactly as the phone does.
 */
import { apiFetch } from "../../../shared/api-client";

/** One number the Company has texted with, and the last thing said. */
export interface MessageThread {
  company_e164: string;
  counterparty_e164: string;
  last_body: string;
  /** inbound | outbound */
  last_direction: string;
  /** queued | sent | delivered | failed | received */
  last_status: string;
  /** ISO-8601 in UTC. */
  last_at: string | null;
}

/** One text, either way. */
export interface TextMessage {
  id: string;
  company_e164: string;
  counterparty_e164: string;
  direction: string;
  body: string;
  status: string;
  /** Why a send failed, in the carrier's words. Empty otherwise. */
  error: string;
  created_at: string | null;
}

export interface MessageThreadPage {
  /** The Company's number the thread is on. */
  number: string;
  /** The other end, as the server normalised it. */
  with: string;
  messages: TextMessage[];
}

export interface SendMessageRequest {
  to: string;
  body: string;
  /** One of the Company's numbers; its first when left out. */
  from?: string;
}

export async function listMessageThreads(signal?: AbortSignal): Promise<MessageThread[]> {
  const res = await apiFetch<{ threads: MessageThread[] }>("/api/admin/messages/threads", { signal });
  return res.threads;
}

export function listMessages(withNumber: string, number?: string, signal?: AbortSignal): Promise<MessageThreadPage> {
  return apiFetch<MessageThreadPage>("/api/admin/messages", {
    query: { with: withNumber, number },
    signal,
  });
}

/**
 * Sends one text. A carrier refusal is an ApiError(502) whose message is
 * the carrier's sentence; the row it left is on the thread as failed.
 */
export async function sendMessage(req: SendMessageRequest): Promise<TextMessage> {
  const res = await apiFetch<{ message: TextMessage }>("/api/admin/messages", { method: "POST", body: req });
  return res.message;
}

/** What the Texts panel needs of the API, so a test can stand it in. */
export interface TextsApi {
  listThreads: typeof listMessageThreads;
  listThread: typeof listMessages;
  send: typeof sendMessage;
}

export const textsApi: TextsApi = {
  listThreads: listMessageThreads,
  listThread: listMessages,
  send: sendMessage,
};
